// utils/homeHelpers.ts
import { Program } from '../components/types';
import { Program as FirestoreProgram } from '../../../../firebase/programFirestoreService';

// Helper function to map firestore status to local status
export const mapFirestoreStatus = (status: FirestoreProgram['status']): 'upcoming' | 'ongoing' | 'completed' => {
  switch (status) {
    case 'published':
    case 'draft':
      return 'upcoming';
    case 'ongoing':
      return 'ongoing';
    case 'completed':
    case 'cancelled':
      return 'completed';
    default:
      return 'upcoming';
  }
};

// Format timestamp to readable date
export const formatDate = (timestamp: any): string => {
  if (!timestamp) return 'TBA';
  
  try {
    let date: Date;
    
    if (timestamp.toDate) {
      date = timestamp.toDate();
    } else if (timestamp instanceof Date) {
      date = timestamp;
    } else if (typeof timestamp === 'string') {
      date = new Date(timestamp);
    } else if (timestamp.seconds) {
      date = new Date(timestamp.seconds * 1000);
    } else {
      date = new Date(timestamp);
    }
    
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'TBA';
  }
};

// Coerce various date/timestamp formats to milliseconds (number)
export const toMillis = (v: any): number => {
  if (!v) return 0;
  
  try {
    if (typeof v === 'number') return v;
    
    if (v?.toMillis && typeof v.toMillis === 'function') {
      return v.toMillis();
    }
    
    if (v?.toDate && typeof v.toDate === 'function') {
      return v.toDate().getTime();
    }
    
    if (v?.seconds && typeof v.seconds === 'number') {
      return v.seconds * 1000;
    }
    
    if (typeof v === 'string') {
      return new Date(v).getTime();
    }
    
    if (v instanceof Date) {
      return v.getTime();
    }
    
    return 0;
  } catch (error) {
    console.error('Error converting to millis:', error, v);
    return 0;
  }
};

// Derive program status from dates
export const deriveStatusFromDates = (
  start: any,
  end: any,
  fallbackStatus?: FirestoreProgram['status']
): 'upcoming' | 'ongoing' | 'completed' => {
  try {
    const now = Date.now();
    const startMs = toMillis(start);
    const endMs = toMillis(end);

    if (startMs && endMs) {
      if (now < startMs) return 'upcoming';
      if (now >= startMs && now <= endMs) return 'ongoing';
      if (now > endMs) return 'completed';
    }

    if (startMs && !endMs) {
      return now >= startMs ? 'ongoing' : 'upcoming';
    }

    return mapFirestoreStatus(fallbackStatus as FirestoreProgram['status']);
  } catch (e) {
    return mapFirestoreStatus(fallbackStatus as FirestoreProgram['status']);
  }
};

// Get program progress percentage
export const getProgramProgress = (program: Program): number => {
  if (!program.capacity || program.capacity === 0) return 0;
  const registered = program.registeredCount || 0;
  return Math.min(Math.round((registered / program.capacity) * 100), 100);
};

// Get status badge color
export const getStatusColor = (status: string) => {
  switch (status) {
    case 'ongoing': return 'bg-gradient-to-br from-primary-900 via-primary-800 to-secondary-900 text-white';
    case 'upcoming': return 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white';
    case 'completed': return 'bg-gradient-to-r from-purple-500 to-pink-500 text-white';
    default: return 'bg-gradient-to-r from-gray-500 to-gray-600 text-white';
  }
};

// Generate gradient colors for badges
export const getBadgeGradient = (index: number) => {
  const gradients = [
    'bg-gradient-to-r from-purple-600 via-pink-500 to-rose-500',
    'bg-gradient-to-r from-blue-600 via-cyan-500 to-teal-400',
    'bg-gradient-to-r from-emerald-500 via-green-500 to-lime-400',
    'bg-gradient-to-r from-orange-500 via-amber-500 to-yellow-400',
    'bg-gradient-to-r from-violet-600 via-purple-500 to-fuchsia-500',
    'bg-gradient-to-r from-rose-600 via-red-500 to-orange-400'
  ];
  return gradients[index % gradients.length];
};

// Get random read time
export const getRandomReadTime = () => {
  const times = ['3 min read', '5 min read', '7 min read', '10 min read'];
  return times[Math.floor(Math.random() * times.length)];
};